<script>
import * as PIXI from 'pixi.js';
import { mapGetters } from 'vuex';
import utils from '@/utils';
import config from '@/config';
import TweenMax, { Power3, Expo, Linear } from 'gsap/TweenMax';

export default {
  name: 'PixiMeteo',
  data() {
    return {
      app: null,

      //interval
      tipsInterval: null,
      colmateInterval: null,
      
      //containers
      brush: null,
      bgContainer: null,
      renderTexture: null,

      //cracks
      crackCollisions: [],
      
      //flags
      isDrawing: false,
      hasMoved: false,

      collisions: 0,
    }
  },
  computed: {
    ...mapGetters({
      viewport: 'App/viewport',
      isTablet: 'App/isTablet',
      showTips: 'Interface/showTips',
    }),
    stage() {
      return this.app.stage;
    },
    headerHeight() {
      return 46;
    },
    verresPositions() {
      return [{x:.63, y:.85}, {x:.3, y:.75}];
    },
    cracksPositions() {
      return [
        {x:.4, y:.5},
        {x:.2, y:.15},
        {x:.8, y:.3},
        {x:.8, y:.8},
        {x:.1, y:.9}
      ];
    }
  },
  mounted() {
    if(this.app) return;

    this.app = new PIXI.Application({
      width: this.viewport.width, height: this.viewport.height - this.headerHeight,
      backgroundColor: 0x000000,
      resolution: 1,
      view: document.querySelector('.pixi-stage'),
      transparent: true,
    });

    this.$nextTick( () => {
      this.init();
      this.resize(this.viewport.width, this.viewport.height);
    });
  },
  beforeDestroy() {
    this.stopTipsTimer();
    this.stopColmateInterval();

    if(this.stage) {
      this.stage.removeChildren();
      this.stage.off();
    }

    if(this.app) {
      this.app.destroy();
      this.app = null;
    }
  },
  methods: {
    init() {
      this.bgContainer = new PIXI.Sprite();
      this.stage.addChild(this.bgContainer);

      //background
      const bgBefore = new PIXI.Sprite(this.$store.getters['Textures/byId']('meteo-bg-step1').texture);
      bgBefore.name = "bgBefore";
      this.bgContainer.addChild(bgBefore);

      const bgAfter = new PIXI.Sprite(this.$store.getters['Textures/byId']('meteo-bg-step2').texture);
      bgAfter.name = "bgAfter";
      this.bgContainer.addChild(bgAfter);

      //Verres
      var verresContainer = new PIXI.Sprite();
      verresContainer.name = "verresContainer";
      this.stage.addChild(verresContainer);

      this.verresPositions.forEach((position, index) => {
        var verre = new PIXI.Sprite(this.$store.getters['Textures/byId'](`meteo-verre${(index + 1)}`).texture);
        verre.name = "verre_" + index;
        verre.position.set(this.viewport.width * position.x, this.viewport.height * position.y);
        verre.scale.set(.5, .5);
        verre.anchor.set(.5, 1);
        verresContainer.addChild(verre);
      });

      //pluie
      var rainTextures = [
        this.$store.getters['Textures/byId']('spsheet-rain').spritesheet.textures["grele1.png"],
        this.$store.getters['Textures/byId']('spsheet-rain').spritesheet.textures["grele2.png"],
        this.$store.getters['Textures/byId']('spsheet-rain').spritesheet.textures["grele3.png"],
      ];

      var pluieSprite = new PIXI.AnimatedSprite(rainTextures);
      pluieSprite.name = "pluie";
      pluieSprite.animationSpeed = .15;
      pluieSprite.scale.set(.45, .45);
      pluieSprite.play();
      this.stage.addChild(pluieSprite);

      
      //---- render texure
      this.renderTexture = PIXI.RenderTexture.create(this.viewport.width, this.viewport.height);

      const rdSprite = new PIXI.Sprite(this.renderTexture);
      rdSprite.name = "rdSprite";
      this.stage.addChild(rdSprite);

      // /*ciel s'assombrit: */
      const delayStartTempete = 3;
      TweenMax.from(bgAfter, 1.5, {alpha:0, ease:Linear.easeNone, delay: delayStartTempete});
      
      //assombrir verres aussi
      TweenMax.to(verresContainer.getChildAt(0), 1.4, {alpha:0.5, overwrite:false, ease:Linear.easeNone, delay: delayStartTempete});
      TweenMax.to(verresContainer.getChildAt(1), 1.4, {alpha:0.5, overwrite:false, ease:Linear.easeNone, delay: delayStartTempete});

      /* verres drop */
      TweenMax.to(verresContainer.getChildAt(0), .5, {x:`+=${this.viewport.width}`, y:"+=60", alpha:0, overwrite:false, rotation: utils.degreeToRadians(125), ease:Expo.easeInOut, delay:delayStartTempete + 1.2}) //big verre
      TweenMax.to(verresContainer.getChildAt(1), .6, {x:`+=${this.viewport.width * 1.01}`, y:"+=100", alpha:0, overwrite:false, rotation: utils.degreeToRadians(105), ease:Expo.easeInOut, delay:delayStartTempete + 1.4}) //small verre

      // /*arrivée pluie: */
      TweenMax.from(pluieSprite, 1.5, {alpha:0, ease:Power3.easeOut, delay: delayStartTempete + 3, onCompleteScope:this, onComplete: this.initCracks});

    },

    initCracks() {
      const crackContainer = new PIXI.Sprite();
      crackContainer.name = "crackContainer";
      this.stage.addChild(crackContainer);

      const textures = [
        [
          this.$store.getters['Textures/byId']('spsheet-crack1').spritesheet.textures['crack1_01.png'],
          this.$store.getters['Textures/byId']('spsheet-crack1').spritesheet.textures['crack1_02.png'],
          this.$store.getters['Textures/byId']('spsheet-crack1').spritesheet.textures['crack1_03.png'],
          this.$store.getters['Textures/byId']('spsheet-crack1').spritesheet.textures['crack1_after.png']
        ],
        [
          this.$store.getters['Textures/byId']('spsheet-crack2').spritesheet.textures['crack2_01.png'],
          this.$store.getters['Textures/byId']('spsheet-crack2').spritesheet.textures['crack2_02.png'],
          this.$store.getters['Textures/byId']('spsheet-crack2').spritesheet.textures['crack2_03.png'],
          this.$store.getters['Textures/byId']('spsheet-crack2').spritesheet.textures['crack2_after.png']
        ],
        [
          this.$store.getters['Textures/byId']('spsheet-crack3').spritesheet.textures['crack3_01.png'],
          this.$store.getters['Textures/byId']('spsheet-crack3').spritesheet.textures['crack3_02.png'],
          this.$store.getters['Textures/byId']('spsheet-crack3').spritesheet.textures['crack3_03.png'],
          this.$store.getters['Textures/byId']('spsheet-crack3').spritesheet.textures['crack3_after.png']
        ]
      ];
      
      const delayApparitionCrack = .5;

      this.cracksPositions.forEach( (position, index) => {
        const texture = textures[utils.randomBetween(0, textures.length-1)];
        const crack = new PIXI.AnimatedSprite(texture);
        crack.interactive = true;
        crack.scale.set(this.isTablet ? .7 : .5);
        crack.anchor.set(.5, .5);
        crack.x = this.viewport.width * position.x;
        crack.y = this.viewport.height * position.y;
        crack.loop = false;
        crack.animationSpeed = .15;
        crack.alpha = 0;

        crack.onFrameChange = (frame) => {
          if(frame === 2) {
            crack.stop();
            crack.off();
          }
        }

        crackContainer.addChild(crack);
        this.crackCollisions.push(0);

        TweenMax.set(crack, {alpha:1, delay:index * delayApparitionCrack});
        TweenMax.delayedCall(index * delayApparitionCrack, () => {
          crack.play();
          const crackSound = "crack" + (Math.round(Math.random()+1));
          this.$store.dispatch('Sound/PLAY', {id:crackSound});
        });
      });

      TweenMax.delayedCall(delayApparitionCrack * this.cracksPositions.length, this.startDrawing.bind(this));
    },

    startDrawing() {
      
      //brush
      this.brush = PIXI.Sprite.from('/assets/tableaux/meteo/brush.png');
      this.brush.anchor.set(.5);
      this.brush.scale.set(.5);
      this.brush.alpha = 0.8;

      this.stage.interactive = true;
      this.stage.on('pointerdown', this.down);
      this.stage.on('pointerup', this.up);
      this.stage.on('pointermove', this.move);

      this.stopTipsTimer();
      this.startTipsTimer();
    },

    stopDrawing() {
      this.stopTipsTimer();
      this.stopColmateInterval();

      this.stage.interactive = false;
      this.stage.off();

      this.$emit('leave');
    },

    startTipsTimer() {
      this.tipsInterval = setInterval(() => {
        this.$store.dispatch('Interface/SHOW_TIPS');
      }, 5000);
    },

    stopTipsTimer() {
      clearInterval(this.tipsInterval);
      if(this.showTips) this.$store.dispatch('Interface/HIDE_TIPS');
    },

    startColmateInterval() {
      this.colmateInterval = setTimeout(() => {
        this.hasMoved = false;
      }, utils.randomBetween(400, 800));
    },

    stopColmateInterval() {
      clearTimeout(this.colmateInterval);
    },

    down(event) {
      this.isDrawing = true;
      this.move(event)
      this.stopTipsTimer();
    },

    up() {
      this.isDrawing = false;
      this.stopTipsTimer();
      this.startTipsTimer();
    },

    move(event) {
      if(!this.isDrawing) return;

      //opacité de la brush 
      const positions = this.cracksPositions.slice();
      const distances = [];

      //Eventuellement ne pas considérer les craques déja colmatées
      positions.forEach((position, index) => {
        var dX = this.brush.position.x - (position.x * this.viewport.width); 
        var dY = this.brush.position.y - (position.y * this.viewport.height); 
        distances[index] = Math.sqrt(Math.pow(dX, 2) + Math.pow(dY, 2));
      });
      
      var min = Math.min(Math.max(parseInt(Math.min(...distances)), 0), 100);
      var alphaCalc = (100 - min) * 0.01;
      var scaleCalc = this.isTablet ? (500 - min * 2) * 0.002 : (500 - min * 2) * 0.001; 
      
      this.brush.alpha = alphaCalc;
      this.brush.scale.set(scaleCalc);

      // //save for collision
      const crackContainer = this.stage.getChildByName("crackContainer");
      crackContainer.children.forEach((child, index) => {
        const hit = this.app.renderer.plugins.interaction.hitTest(event.data.global, child);
        if(hit && child.currentFrame != 3) {
          this.crackCollisions[index]++;
        }
      });

      //check collisions
      let crackRemoved = 0;
      this.crackCollisions.forEach((total, index) => {
        const child = crackContainer.getChildAt(index);
        if(total >= config.SETTINGS.Meteo.totalCollision && child.currentFrame != 3) {
          child.gotoAndStop(3);
          this.resetTexture();
        }

        if(child.currentFrame === 3) crackRemoved++;
      });

      //check how many cracks are still in the stage
      if(crackRemoved >= 3) {
        this.stopDrawing();        
        return;
      }

      this.brush.position.copyFrom(event.data.global);
      this.app.renderer.render(this.brush, this.renderTexture, false, null, false);
     
      // son doigt
      if(!this.hasMoved){
        this.hasMoved = true;
        this.$store.dispatch('Sound/PLAY', {id: Math.random() > .5 ? 'colmate1' : 'colmate2', rate:Math.random()*0.8+0.7})

        this.startColmateInterval();
      }
    },
    resetTexture() {
      this.app.renderer.render(this.brush, this.renderTexture, true, null, false);
    },

    resize(w, h) {
      this.app.renderer.resize(w, h - this.headerHeight);

      const bgContainerIds = [{id:'bgBefore', keepRatio:true, toTop:false}, {id:'bgAfter', keepRatio: true, toTop:false}];
      bgContainerIds.forEach( asset => {
        utils.toFullscreen(this.bgContainer.getChildByName(asset.id), {w, h, keepRatio:asset.keepRatio, toTop:asset.toTop});
      });

      const stageIds = [{id:'pluie'}];
      stageIds.forEach( asset => {
        utils.toFullscreen(this.stage.getChildByName(asset.id), {w, h});
      });

      //replace verres
      const verresContainer = this.stage.getChildByName("verresContainer");
      this.verresPositions.forEach((position, index) => {
        const verre = verresContainer.getChildByName("verre_" + index);
        verre.position.set(this.viewport.width * position.x, this.viewport.height * position.y);
      });

      //replace cracks
      const crackContainer = this.stage.getChildByName("crackContainer");
      if(crackContainer) {
        this.cracksPositions.forEach( (position, index) => {
          const crack = crackContainer.getChildAt(index);
            crack.x = w * position.x;
            crack.y = h * position.y;
         });
      }
    },
  }
}
</script>

<template>
  <canvas ref='Canvas' :width="viewport.width" :height="viewport.height - headerHeight" class='pixi-stage' />
</template>

<style scoped lang="scss">
.pixi-stage {
  width: 100%;
  height: 100%;
  position: relative;
  top: 0;
  left: 0px;
}
</style>
